/**
 * ResetPasswordPage — atterrissage du lien envoyé par /api/auth/forgot-password.
 * URL attendue : /reset-password?token=...
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Lien invalide : aucun token fourni.");
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/", { replace: true }), 2500);
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "#F9F8F6" }} data-testid="reset-password-page">
      <div className="w-full max-w-md space-y-5 bg-white border border-black/5 rounded-2xl p-6 shadow-sm">
        <div className="text-center space-y-1">
          <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-14 mx-auto mb-2" />
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif", color: "#1A1A1A" }}>
            Réinitialiser le mot de passe
          </h1>
          <p className="text-xs text-neutral-500">Choisissez un nouveau mot de passe pour votre compte.</p>
        </div>

        {done ? (
          <div className="text-center space-y-3 py-4">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
            <p className="text-sm font-semibold">Mot de passe mis à jour !</p>
            <p className="text-xs text-neutral-500">Redirection vers la page de connexion...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="pwd" className="text-[11px] text-neutral-600">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <PasswordInput id="pwd" data-testid="reset-pwd-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="h-10 pl-9 text-sm" autoComplete="new-password" />
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-[11px] text-neutral-600">Confirmation</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <PasswordInput id="confirm" data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="h-10 pl-9 text-sm" autoComplete="new-password" />
              </div>
            </div>
            {error && <p data-testid="reset-error" className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</p>}
            <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full h-10 rounded-full text-sm font-semibold" style={{ background: "#2D6A4F", color: "white" }}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Patientez...</> : "Mettre à jour"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
